<template>
  <section class="agency-users">
    <div class="agency-users__toolbar mb-3">
      <el-select v-model="status" placeholder="All statuses" clearable aria-label="User status" :disabled="loading || !!changingId">
        <el-option label="Active" value="active" />
        <el-option label="Disabled" value="disabled" />
      </el-select>
      <button class="btn btn-sm btn-default" :disabled="loading || !!changingId" @click="load">Refresh</button>
    </div>
    <p v-if="error" class="text-danger" role="alert">{{ error }} <button class="btn btn-sm btn-default" @click="load">Retry</button></p>
    <p v-if="loading">Loading users…</p>
    <p v-if="actionError" class="text-danger" role="alert">{{ actionError }}</p>
    <template v-else-if="loaded">
      <p>{{ pagination.total_count }} users</p>
      <p v-if="!users.length">No users match these filters.</p>
      <table v-else class="table agency-users__table">
        <thead><tr><th>User</th><th>Contacts</th><th>Role</th><th>Status</th><th>Access</th><th v-if="canManageAgencies">Actions</th></tr></thead>
        <tbody><tr v-for="user in users" :key="user.id">
          <td data-label="User">
            <strong>{{ [user.first_name, user.last_name].filter(Boolean).join(' ') || '—' }}</strong>
            <div class="agency-users__secondary">#{{ user.id }}</div>
          </td>
          <td data-label="Contacts">
            <div>{{ user.email || '—' }}</div>
            <div class="agency-users__secondary">{{ user.phone || '—' }}</div>
          </td>
          <td data-label="Role">{{ roles[user.role] || user.role || '—' }}</td>
          <td data-label="Status">
            <span class="agency-users__status" :class="{ 'agency-users__status--active': user.status === 'active' }">{{ user.status === 'active' ? 'Active' : 'Disabled' }}</span>
            <div class="agency-users__secondary">{{ user.confirmed ? 'Confirmed' : 'Not confirmed' }}</div>
          </td>
          <td data-label="Access">
            <details v-if="user.languages.length"><summary>{{ user.languages.length }} languages</summary><div class="agency-users__values">{{ user.languages.join(', ') }}</div></details>
            <div v-else class="agency-users__secondary">No languages</div>
            <details v-if="user.country_codes.length"><summary>{{ user.country_codes.length }} countries</summary><div class="agency-users__values">{{ user.country_codes.join(', ') }}</div></details>
            <div v-else class="agency-users__secondary">No countries</div>
          </td>
          <td v-if="canManageAgencies" data-label="Actions" class="agency-users__actions">
            <button class="btn btn-sm" :class="user.status === 'active' ? 'btn-warning' : 'btn-success'" :disabled="!!changingId || !!impersonatingId || loading" @click="changeStatus(user)">{{ changingId === user.id ? 'Saving…' : user.status === 'active' ? 'Deactivate' : 'Activate' }}</button>
            <button v-if="user.status === 'active'" type="button" class="btn btn-primary btn-sm" :disabled="!!changingId || !!impersonatingId || loading" :title="`Log in as user #${user.id}`" :aria-label="`Log in as user #${user.id}`" @click="impersonate(user)">
              <i :class="impersonatingId === user.id ? 'tim-icons icon-refresh-02 agency-users__spinner' : 'fa fa-sign-in-alt'"></i>
            </button>
          </td>
        </tr></tbody>
      </table>
      <el-pagination v-if="pagination.page_count > 1" :disabled="!!changingId" background layout="prev, pager, next" :page-size="perPage" :current-page="page" :page-count="pagination.page_count" @current-change="changePage" />
    </template>
  </section>
</template>
<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import { openPartnerCabinet } from '@/components/Agencies/agencyImpersonation';
import API from '@/api/modules/agencies';
export default {
  mixins: [agencyAccess],
  props: { agencyId: { type: [String, Number], required: true } },
  data: () => ({ users: [], pagination: {}, page: 1, perPage: 20, status: '', loading: false, loaded: false, error: '', actionError: '', changingId: null, impersonatingId: null, requestVersion: 0, actionVersion: 0,
    roles: { agency_owner: 'Owner', agency_employees_manager: 'Users manager', agency_order_manager: 'Order manager', agency_general_employee: 'Employee' } }),
  created() { this.load(); },
  beforeDestroy() { this.requestVersion++; this.actionVersion++; },
  watch: { agencyId() { this.actionVersion++; this.changingId = null; this.impersonatingId = null; this.actionError = ''; this.page = 1; this.load(); }, status() { this.page = 1; this.load(); } },
  methods: {
    async impersonate(user) {
      if (!this.canManageAgencies || user.status !== 'active' || this.changingId || this.impersonatingId || this.loading
        || !this.users.some(row => row.id === user.id && row.status === 'active')) return;
      const agencyId = this.agencyId;
      const version = ++this.actionVersion;
      this.impersonatingId = user.id;
      try {
        const { data } = await API.impersonateUser(agencyId, user.id);
        if (version !== this.actionVersion || !this.canManageAgencies || agencyId !== this.agencyId) return;
        openPartnerCabinet(data);
      } catch (e) {
        if (version === this.actionVersion) this.$notify({ type: 'error', title: e?.response?.data?.error?.code || e.message || 'Impersonate failed' });
      } finally { if (version === this.actionVersion) this.impersonatingId = null; }
    },
    async changeStatus(user) {
      if (!this.canManageAgencies || this.changingId || this.impersonatingId || this.loading || !this.users.some(row => row.id === user.id && row.status === user.status)) return;
      const id = this.agencyId; const target = user.status === 'active' ? 'disabled' : 'active';
      const version = ++this.actionVersion;
      this.changingId = user.id; this.actionError = '';
      try {
        await this.$confirm(`${target === 'active' ? 'Activate' : 'Deactivate'} user #${user.id}?`, 'User status', { confirmButtonText: 'Confirm', cancelButtonText: 'Cancel', type: 'warning' });
        if (version !== this.actionVersion || !this.canManageAgencies) return;
        // Invalidate pending list snapshots before the effect; display only a fresh authoritative list.
        this.requestVersion++;
        await API.setAgencyUserStatus(id, user.id, target, user.status);
        if (version !== this.actionVersion) return;
        this.page = 1; await this.load();
      } catch (e) {
        if (version !== this.actionVersion || e === 'cancel' || e === 'close') return;
        this.actionError = 'Status was not verified. Refresh before retrying.';
        this.page = 1; await this.load();
      } finally { if (version === this.actionVersion) this.changingId = null; }
    },
    changePage(page) { this.page = page; this.load(); },
    async load() {
      const version = ++this.requestVersion;
      const id = this.agencyId; const page = this.page; const status = this.status;
      this.loading = true; this.loaded = false; this.error = ''; this.users = []; this.pagination = {};
      try {
        const result = await API.getAgencyUsers(id, { page, per_page: this.perPage, ...(status ? { status } : {}) });
        if (version !== this.requestVersion) return;
        this.users = result.users; this.pagination = result.pagination; this.loaded = true;
      } catch (e) {
        if (version === this.requestVersion) this.error = 'Could not load users. Refresh or retry.';
      } finally { if (version === this.requestVersion) this.loading = false; }
    },
  },
};
</script>

<style scoped>
.agency-users { min-width: 0; }
.agency-users__toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.agency-users__toolbar .el-select { width: 220px; max-width: 100%; }
.agency-users__table { width: 100%; table-layout: fixed; }
.agency-users__table th:nth-child(1) { width: 18%; }
.agency-users__table th:nth-child(2) { width: 26%; }
.agency-users__table th:nth-child(3) { width: 14%; }
.agency-users__table th:nth-child(4) { width: 15%; }
.agency-users__table th:nth-child(6) { width: 180px; }
.agency-users__table td { vertical-align: top; overflow-wrap: anywhere; }
.agency-users__table .btn { white-space: nowrap; margin: 0; }
.agency-users__actions { white-space: nowrap; }
.agency-users__actions .btn + .btn { margin-left: 6px; }
.agency-users__spinner { display: inline-block; animation: agency-users-spin 1s linear infinite; }
@keyframes agency-users-spin { to { transform: rotate(360deg); } }
.agency-users__secondary { font-size: 12px; margin-top: 4px; }
.agency-users__status { display: inline-block; padding: 2px 7px; border-radius: 4px; background: #eef0f3; color: #4c566a; }
.agency-users__status--active { background: #e6f5e9; color: #28633a; }
.agency-users summary { cursor: pointer; color: #2879b9; }
.agency-users__values { max-height: 120px; overflow: auto; margin: 6px 0; padding: 6px; background: #f6f7fa; }
@media (max-width: 900px) {
  .agency-users__table, .agency-users__table tbody { display: block; }
  .agency-users__table thead { display: none; }
  .agency-users__table tr { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); border: 1px solid #ddd; border-radius: 6px; margin-bottom: 12px; padding: 8px; }
  .agency-users__table td { display: block; border: 0; padding: 8px; min-width: 0; }
  .agency-users__table td::before { content: attr(data-label); display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; margin-bottom: 5px; }
  .agency-users__table td[data-label="Contacts"] { grid-column: 1 / -1; grid-row: 2; }
  .agency-users__table td[data-label="Actions"] { grid-column: 1 / -1; }
}
</style>
